import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  compareDateOnlyStringsDesc,
  dateOnlyStringToCalendarDate,
  formatDateOnlyForDisplay,
  localCalendarDateToIsoDateString,
  normalizeCalendarDayToLocalNoon,
  normalizeToIsoDateOnly,
} from "helpers/date";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { useContext, useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  useGetContractFeeScheduleQuery,
  useGetContractFeeSchedulesQuery,
  useUpdateContractFeeScheduleMutation,
  useUploadCarveoutMutation,
  useDeleteCarveoutMutation,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
  IContractTermInGroup,
  RateCalculation,
  RateType,
  normalizeRateType,
  resolveRateCalculationBaseRateDisplayName,
} from "store/queries/contractFeeSchedules";
import { useGetAllExplodingFeeSchedulesQuery } from "store/queries/explodingFeeSchedule";
import { useGetInpatientRulesetsQuery } from "store/queries/inpatientRulesets";
import { useGetAllPackageRulesetsQuery } from "store/queries/packageRuleset";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetCaseRateRulesetsQuery } from "store/queries/pricing";
import { useGetAllDiscountRulesetsQuery } from "store/queries/procedureDiscountRuleset";
import { useGetAllProvidersQuery } from "store/queries/providers";

interface IFormData {
  providerId: string;
  payerId: number | null;
  effectiveDate: Date | null;
  nextUpdateDate: Date | null;
  rateCalculations: RateCalculation[];
  carveoutFiles: File[];
  attachmentFiles: File[];
  mpdrRulesetIds: string[];
  caseRateRulesetIds: string[];
  explodingFeeScheduleRulesetIds: string[];
  packageRulesetIds: string[];
  inpatientRulesetIds: string[];
  medicareAdvantagePayerIds: number[];
  notes?: string;
}

export function EditContractFeeSchedulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, contractId } = useParams<{ id: string; contractId?: string }>();

  // In hospital context, use contractId; in admin context, use id
  const actualContractId = contractId || id;
  const hospitalId = contractId ? id : null; // If contractId exists, then id is the hospital ID
  useDocumentTitle("Edit Contract Fee Schedule");
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);

  // Form state
  const [formData, setFormData] = useState<IFormData>({
    providerId: "",
    payerId: null,
    effectiveDate: null,
    nextUpdateDate: null,
    rateCalculations: [],
    carveoutFiles: [],
    attachmentFiles: [],
    mpdrRulesetIds: [],
    caseRateRulesetIds: [],
    explodingFeeScheduleRulesetIds: [],
    packageRulesetIds: [],
    inpatientRulesetIds: [],
    medicareAdvantagePayerIds: [],
  });

  // New rate calculation state
  const [newRateCalc, setNewRateCalc] = useState<{
    baseRateFileId: string;
    ratePercent: number;
    rateType: RateType;
  }>({
    baseRateFileId: "",
    ratePercent: 100,
    rateType: "percent",
  });

  // Data fetching
  const { data: contractData, isLoading: isLoadingContract } =
    useGetContractFeeScheduleQuery(actualContractId!);
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();
  const { data: baseRateFiles } = useGetAllBaseRateFilesQuery({
    accountId: hospitalId ?? undefined,
  });
  const { data: mpdrRulesets } = useGetAllDiscountRulesetsQuery();
  const { data: caseRateRulesets } = useGetCaseRateRulesetsQuery();
  const { data: packageRulesetsAll } = useGetAllPackageRulesetsQuery();
  const { data: inpatientRulesetsAll } = useGetInpatientRulesetsQuery();
  const { data: explodingFeeSchedulesAll } =
    useGetAllExplodingFeeSchedulesQuery();
  const [updateContract] = useUpdateContractFeeScheduleMutation();
  const [uploadCarveout] = useUploadCarveoutMutation();
  const [deleteCarveout] = useDeleteCarveoutMutation();
  const [uploadAttachment] = useUploadAttachmentMutation();
  const [deleteAttachment] = useDeleteAttachmentMutation();

  const loadedContract = contractData?.data;
  const passedTerms = (
    location.state as { termsInGroup?: IContractTermInGroup[] } | null
  )?.termsInGroup;

  const { data: contractList = [] } = useGetContractFeeSchedulesQuery(
    {
      providerId: loadedContract?.providerId,
      payerId: loadedContract?.payerId,
    },
    {
      skip: !loadedContract?.providerId || loadedContract?.payerId == null,
    }
  );

  const termsFromList = useMemo(() => {
    if (!contractList.length || !actualContractId) return undefined;
    const row = contractList.find((r) => {
      if (r.id === actualContractId) return true;
      // eslint-disable-next-line no-underscore-dangle
      return !!r._termsInGroup?.some((t) => t.id === actualContractId);
    });
    // eslint-disable-next-line no-underscore-dangle
    return row?._termsInGroup;
  }, [contractList, actualContractId]);

  /** Prefer API list so new terms appear after create; use navigation state only until list loads. */
  const termsInGroup = useMemo(() => {
    if (termsFromList && termsFromList.length > 0) return termsFromList;
    if (passedTerms && passedTerms.length > 0) return passedTerms;
    return termsFromList;
  }, [termsFromList, passedTerms]);

  const termSwitcherOptions = useMemo(() => {
    if (!termsInGroup?.length) return [];
    return [...termsInGroup]
      .sort((a, b) =>
        compareDateOnlyStringsDesc(a.effectiveDate, b.effectiveDate)
      )
      .map((t) => ({
        label: `${formatDateOnlyForDisplay(t.effectiveDate)}${
          t.endDate ? ` – ${formatDateOnlyForDisplay(t.endDate)}` : ""
        } · ${t.status}`,
        value: t.id,
      }));
  }, [termsInGroup]);

  const effectiveDateDuplicatesOtherTerm = useMemo(() => {
    if (!formData.effectiveDate || !actualContractId) return false;
    const effectiveIso = localCalendarDateToIsoDateString(
      formData.effectiveDate
    );
    if (!effectiveIso) return false;
    return (
      termsInGroup?.some((t) => {
        if (t.id === actualContractId) return false;
        const other = normalizeToIsoDateOnly(t.effectiveDate);
        return other !== undefined && other === effectiveIso;
      }) ?? false
    );
  }, [formData.effectiveDate, termsInGroup, actualContractId]);

  // Track selected provider (for backwards compat when rateType is missing)
  const selectedProvider = providers?.data?.find(
    (p: any) => p.id === formData.providerId
  );

  // Loading state for submit button
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track existing rulesets for selected provider/payer
  const [existingMpdrRulesets, setExistingMpdrRulesets] = useState<any[]>([]);
  const [existingCaseRateRulesets, setExistingCaseRateRulesets] = useState<
    any[]
  >([]);
  const [existingPackageRulesets, setExistingPackageRulesets] = useState<any[]>(
    []
  );
  const [existingInpatientRulesets, setExistingInpatientRulesets] = useState<
    any[]
  >([]);
  const [existingExplodingRulesets, setExistingExplodingRulesets] = useState<
    any[]
  >([]);

  // Initialize form data from fetched contract
  useEffect(() => {
    if (contractData?.data) {
      const contract = contractData.data;
      setFormData({
        providerId: contract.providerId,
        payerId: contract.payerId,
        effectiveDate: dateOnlyStringToCalendarDate(contract.effectiveDate),
        nextUpdateDate: contract.nextUpdateDate
          ? dateOnlyStringToCalendarDate(contract.nextUpdateDate)
          : null,
        rateCalculations: (contract.rateCalculations || []).map((calc) => ({
          ...calc,
          ratePercent: Number(calc.ratePercent),
          rateType: normalizeRateType(calc.rateType),
        })),
        carveoutFiles: [],
        attachmentFiles: [],
        mpdrRulesetIds: contract.mpdrRulesetIds || [],
        caseRateRulesetIds: contract.caseRateRulesetIds || [],
        explodingFeeScheduleRulesetIds:
          contract.explodingFeeScheduleRulesetIds || [],
        packageRulesetIds: contract.packageRulesetIds || [],
        inpatientRulesetIds: contract.inpatientRulesetIds || [],
        notes: contract.notes || "",
        medicareAdvantagePayerIds:
          contract.medicareAdvantagePlans?.map(
            (link: any) => link.medicareAdvantagePlanId
          ) || [],
      });
    }
  }, [contractData]);

  // Clean up deleted ruleset IDs from form data
  useEffect(() => {
    if (mpdrRulesets?.data && formData.mpdrRulesetIds.length > 0) {
      const availableIds = mpdrRulesets.data.map((r: any) => r.id);
      const validIds = formData.mpdrRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.mpdrRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          mpdrRulesetIds: validIds,
        }));
      }
    }
  }, [mpdrRulesets?.data, formData.mpdrRulesetIds]);

  useEffect(() => {
    if (caseRateRulesets?.data && formData.caseRateRulesetIds.length > 0) {
      const availableIds = caseRateRulesets.data.map((r: any) => r.id);
      const validIds = formData.caseRateRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.caseRateRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          caseRateRulesetIds: validIds,
        }));
      }
    }
  }, [caseRateRulesets?.data, formData.caseRateRulesetIds]);

  useEffect(() => {
    if (
      explodingFeeSchedulesAll?.data &&
      formData.explodingFeeScheduleRulesetIds.length > 0
    ) {
      const availableIds = explodingFeeSchedulesAll.data.map((r: any) => r.id);
      const validIds = formData.explodingFeeScheduleRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.explodingFeeScheduleRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          explodingFeeScheduleRulesetIds: validIds,
        }));
      }
    }
  }, [explodingFeeSchedulesAll?.data, formData.explodingFeeScheduleRulesetIds]);

  useEffect(() => {
    if (packageRulesetsAll?.data && formData.packageRulesetIds.length > 0) {
      const availableIds = packageRulesetsAll.data.map((r: any) => r.id);
      const validIds = formData.packageRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.packageRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          packageRulesetIds: validIds,
        }));
      }
    }
  }, [packageRulesetsAll?.data, formData.packageRulesetIds]);

  useEffect(() => {
    if (inpatientRulesetsAll?.data && formData.inpatientRulesetIds.length > 0) {
      const availableIds = inpatientRulesetsAll.data.map((r: any) => r.id);
      const validIds = formData.inpatientRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.inpatientRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          inpatientRulesetIds: validIds,
        }));
      }
    }
  }, [inpatientRulesetsAll?.data, formData.inpatientRulesetIds]);

  // Fetch existing rulesets when provider/payer changes
  useEffect(() => {
    if (formData.providerId && formData.payerId) {
      // Filter existing MPDR rulesets for this provider/payer combination
      const filteredMpdrRulesets =
        mpdrRulesets?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingMpdrRulesets(filteredMpdrRulesets);

      // Filter existing case rate rulesets for this provider/payer combination
      const filteredCaseRateRulesets =
        caseRateRulesets?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingCaseRateRulesets(filteredCaseRateRulesets);

      // Filter existing package rulesets for this provider/payer combination
      const filteredPackageRulesets =
        packageRulesetsAll?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingPackageRulesets(filteredPackageRulesets);

      // Filter existing inpatient rulesets for this provider/payer combination
      const filteredInpatientRulesets =
        inpatientRulesetsAll?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingInpatientRulesets(filteredInpatientRulesets);

      // Filter existing exploding fee schedule rulesets for this provider/payer combination
      const filteredExplodingRulesets =
        explodingFeeSchedulesAll?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingExplodingRulesets(filteredExplodingRulesets);
    } else {
      setExistingMpdrRulesets([]);
      setExistingCaseRateRulesets([]);
      setExistingPackageRulesets([]);
      setExistingInpatientRulesets([]);
      setExistingExplodingRulesets([]);
    }
  }, [
    formData.providerId,
    formData.payerId,
    mpdrRulesets?.data,
    caseRateRulesets?.data,
    packageRulesetsAll?.data,
    inpatientRulesetsAll?.data,
    explodingFeeSchedulesAll?.data,
  ]);

  // Options for dropdowns
  const providerOptions = useMemo(() => {
    return (
      providers?.data?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [providers]);

  const payerOptions = useMemo(() => {
    return (
      payers?.data?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [payers]);

  const baseRateFileOptions = useMemo(() => {
    return (
      baseRateFiles?.data?.map((f) => ({
        label: f.displayName,
        value: f.id,
      })) || []
    );
  }, [baseRateFiles]);

  const mpdrRulesetOptions = useMemo(() => {
    return (
      mpdrRulesets?.data?.map((ruleset: any) => ({
        label: `${ruleset.groupName || "Unnamed Ruleset"} (${
          ruleset.provider?.name
        } - ${ruleset.payer?.name})`,
        value: ruleset.id,
        // Store searchable text for filtering
        searchText: `${ruleset.groupName || ""} ${
          ruleset.provider?.name || ""
        } ${ruleset.payer?.name || ""}`.toLowerCase(),
      })) || []
    );
  }, [mpdrRulesets]);

  const caseRateRulesetOptions = useMemo(() => {
    return (
      caseRateRulesets?.data?.map((ruleset: any) => ({
        label: `${ruleset.groupName || "Unnamed Ruleset"} (${
          ruleset.provider?.name
        } - ${ruleset.payer?.name})`,
        value: ruleset.id,
        // Store searchable text for filtering
        searchText: `${ruleset.groupName || ""} ${
          ruleset.provider?.name || ""
        } ${ruleset.payer?.name || ""}`.toLowerCase(),
      })) || []
    );
  }, [caseRateRulesets]);

  const explodingRulesetOptions = useMemo(() => {
    return (
      explodingFeeSchedulesAll?.data?.map((ruleset: any) => ({
        label: `${ruleset.groupName || "Unnamed Exploding Fee Schedule"} (${
          ruleset.provider?.name
        } - ${ruleset.payer?.name})`,
        value: ruleset.id,
        searchText: `${ruleset.groupName || ""} ${
          ruleset.provider?.name || ""
        } ${ruleset.payer?.name || ""}`.toLowerCase(),
      })) || []
    );
  }, [explodingFeeSchedulesAll]);

  const packageRulesetOptions = useMemo(() => {
    return (
      packageRulesetsAll?.data?.map((ruleset: any) => ({
        label: `${ruleset.groupName || "Unnamed Package Ruleset"} (${
          ruleset.provider?.name
        } - ${ruleset.payer?.name})`,
        value: ruleset.id,
        searchText: `${ruleset.groupName || ""} ${
          ruleset.provider?.name || ""
        } ${ruleset.payer?.name || ""}`.toLowerCase(),
      })) || []
    );
  }, [packageRulesetsAll]);

  const inpatientRulesetOptions = useMemo(() => {
    return (
      inpatientRulesetsAll?.data?.map((ruleset: any) => ({
        label: `${ruleset.name || "Unnamed Inpatient Ruleset"} (${
          ruleset.provider?.name
        } - ${ruleset.payer?.name})`,
        value: ruleset.id,
        searchText: `${ruleset.name || ""} ${ruleset.provider?.name || ""} ${
          ruleset.payer?.name || ""
        }`.toLowerCase(),
      })) || []
    );
  }, [inpatientRulesetsAll]);

  const medicareAdvantagePayerOptions = useMemo(() => {
    return (
      payers?.data
        ?.filter((p: any) => p.type === "UhcMedicareAdvantage")
        ?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [payers]);

  const selectedPayer = payers?.data?.find(
    (p: any) => p.id === formData.payerId
  );
  const isMedicarePayer = selectedPayer?.type === "OriginalMedicare";

  const handleInputChange = (field: keyof IFormData, value: any) => {
    setFormData((prev) => {
      let nextVal = value;
      if (
        (field === "effectiveDate" || field === "nextUpdateDate") &&
        value instanceof Date
      ) {
        nextVal = normalizeCalendarDayToLocalNoon(value);
      }
      const updates: any = { [field]: nextVal };

      // Clear Medicare Advantage payers if payer is changed and is not Medicare
      if (field === "payerId") {
        const newPayer = payers?.data?.find((p: any) => p.id === value);
        if (newPayer?.type !== "OriginalMedicare") {
          updates.medicareAdvantagePayerIds = [];
        }
      }

      return { ...prev, ...updates };
    });
  };

  const addNewRateCalculation = () => {
    if (newRateCalc.ratePercent <= 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Invalid Rate Calculation",
        detail:
          newRateCalc.rateType === "unit"
            ? "Please enter a valid unit price."
            : "Please enter a valid rate percentage.",
      });
      return;
    }

    const newCalc: RateCalculation = {
      id: `temp-${Date.now()}`, // Temporary ID for UI purposes
      baseRateFileId: newRateCalc.baseRateFileId || undefined,
      ratePercent: Number(newRateCalc.ratePercent),
      rateType: newRateCalc.rateType,
      sortOrder: formData.rateCalculations.length,
    };

    // Add the new rate calculation to the form data
    setFormData((prev) => ({
      ...prev,
      rateCalculations: [...prev.rateCalculations, newCalc],
    }));

    // Reset form
    setNewRateCalc({
      baseRateFileId: "",
      ratePercent: 100,
      rateType: "percent",
    });
  };

  const removeRateCalculation = (index: number) => {
    // Remove from form data
    setFormData((prev) => ({
      ...prev,
      rateCalculations: prev.rateCalculations.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.providerId) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a provider.",
      });
      return;
    }

    if (!formData.payerId) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a payer.",
      });
      return;
    }

    if (!formData.effectiveDate) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Effective date is required.",
      });
      return;
    }

    const effectiveIso = localCalendarDateToIsoDateString(
      formData.effectiveDate
    );
    if (!effectiveIso) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Effective date is invalid.",
      });
      return;
    }

    const duplicatesAnotherTerm = termsInGroup?.some((t) => {
      if (t.id === actualContractId) return false;
      const other = normalizeToIsoDateOnly(t.effectiveDate);
      return other !== undefined && other === effectiveIso;
    });
    if (duplicatesAnotherTerm) {
      return;
    }

    try {
      setIsLoading(true);
      setIsSubmitting(true);

      // Filter ruleset IDs to only include ones that still exist
      const availableMpdrIds = mpdrRulesets?.data?.map((r: any) => r.id) || [];
      const availableCaseRateIds =
        caseRateRulesets?.data?.map((r: any) => r.id) || [];

      const validMpdrIds = formData.mpdrRulesetIds.filter((id) =>
        availableMpdrIds.includes(id)
      );
      const validCaseRateIds = formData.caseRateRulesetIds.filter((id) =>
        availableCaseRateIds.includes(id)
      );

      const updateData = {
        providerId: formData.providerId,
        payerId: formData.payerId,
        effectiveDate: effectiveIso,
        nextUpdateDate: localCalendarDateToIsoDateString(
          formData.nextUpdateDate
        ),
        rateCalculations: formData.rateCalculations.map((calc) => ({
          baseRateFileId: calc.baseRateFileId,
          ratePercent: Number(calc.ratePercent),
          rateType: calc.rateType ?? "percent",
          sortOrder: calc.sortOrder || 0,
        })),
        mpdrRulesetIds: validMpdrIds.length > 0 ? validMpdrIds : undefined,
        caseRateRulesetIds:
          validCaseRateIds.length > 0 ? validCaseRateIds : undefined,
        explodingFeeScheduleRulesetIds:
          formData.explodingFeeScheduleRulesetIds.length > 0
            ? formData.explodingFeeScheduleRulesetIds
            : undefined,
        packageRulesetIds:
          formData.packageRulesetIds.length > 0
            ? formData.packageRulesetIds
            : undefined,
        inpatientRulesetIds:
          formData.inpatientRulesetIds.length > 0
            ? formData.inpatientRulesetIds
            : undefined,
        medicareAdvantagePayerIds: isMedicarePayer
          ? formData.medicareAdvantagePayerIds
          : undefined,
        notes: formData.notes || "",
      };

      await updateContract({ id: actualContractId!, ...updateData }).unwrap();

      // Upload carveout files if any were selected
      if (formData.carveoutFiles.length > 0) {
        try {
          toastElement?.show({
            severity: "info",
            summary: "Uploading Carveouts",
            detail: `Uploading ${formData.carveoutFiles.length} carveout file(s)...`,
          });

          // Upload all carveout files in parallel
          const uploadPromises = formData.carveoutFiles.map((file) =>
            uploadCarveout({
              contractId: actualContractId!,
              file,
            }).unwrap()
          );

          await Promise.all(uploadPromises);

          toastElement?.show({
            severity: "success",
            summary: "Contract Updated",
            detail: `Contract and ${formData.carveoutFiles.length} carveout file(s) uploaded successfully.`,
          });
        } catch (carveoutError) {
          toastElement?.show({
            severity: "warn",
            summary: "Contract Updated",
            detail:
              "Contract was updated but some carveout files failed to upload.",
          });
        }
      }

      // Upload attachment files if any were selected
      if (formData.attachmentFiles.length > 0) {
        try {
          toastElement?.show({
            severity: "info",
            summary: "Uploading Attachments",
            detail: `Uploading ${formData.attachmentFiles.length} attachment file(s)...`,
          });

          const attachmentPromises = formData.attachmentFiles.map((file) =>
            uploadAttachment({
              contractId: actualContractId!,
              file,
            }).unwrap()
          );

          await Promise.all(attachmentPromises);
        } catch (attachmentError) {
          toastElement?.show({
            severity: "warn",
            summary: "Contract Updated",
            detail:
              "Contract was updated but some attachment files failed to upload.",
          });
        }
      }

      if (
        formData.carveoutFiles.length === 0 &&
        formData.attachmentFiles.length === 0
      ) {
        toastElement?.show({
          severity: "success",
          summary: "Contract Updated",
          detail: "Contract fee schedule has been updated successfully.",
        });
      } else {
        toastElement?.show({
          severity: "success",
          summary: "Contract Updated",
          detail: "Contract fee schedule and files uploaded successfully.",
        });
      }

      const contractUrl = hospitalId
        ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}`
        : `/payer-rules/contracts/${actualContractId}`;
      navigate(contractUrl);
    } catch (error: any) {
      const errorMessage =
        error?.data?.messages?.[0] ||
        error?.data?.message ||
        "Failed to update contract fee schedule.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const getRateTypeForDisplay = (calc: RateCalculation): RateType =>
    normalizeRateType(calc.rateType);

  const rateCalculationBodyTemplate = (
    rowData: RateCalculation,
    options: any
  ) => {
    const rateType = getRateTypeForDisplay(rowData);
    return (
      <div className="flex justify-content-between align-items-center">
        <div>
          <div className="font-medium">
            {resolveRateCalculationBaseRateDisplayName(
              rowData,
              baseRateFiles?.data
            )}
          </div>
          <div className="text-sm">
            {rateType === "unit"
              ? `Price per Unit: ${rowData.ratePercent / 100}`
              : `Rate: ${rowData.ratePercent}%`}
          </div>
        </div>
        {rowData.id && (
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger p-button-text"
            onClick={() => removeRateCalculation(options.rowIndex)}
          />
        )}
      </div>
    );
  };

  const carveoutFileActionsTemplate = (rowData: any) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={async () => {
        try {
          setIsLoading(true);
          await deleteCarveout({
            contractId: actualContractId!,
            carveoutId: rowData.id,
          }).unwrap();
          toastElement?.show({
            severity: "success",
            summary: "Carveout Removed",
            detail: "Carveout file has been removed successfully.",
          });
        } catch (error: any) {
          const errorMessage =
            error?.data?.messages?.[0] ||
            error?.data?.message ||
            "Failed to remove carveout file.";
          toastElement?.show({
            severity: "error",
            summary: "Error",
            detail: errorMessage,
          });
        } finally {
          setIsLoading(false);
        }
      }}
    />
  );

  const newCarveoutFileActionsTemplate = (rowData: File, options: any) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={() => {
        const newFiles = formData.carveoutFiles.filter(
          (_, index) => index !== options.rowIndex
        );
        handleInputChange("carveoutFiles", newFiles);
      }}
    />
  );

  const attachmentFileActionsTemplate = (rowData: any) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={async () => {
        try {
          setIsLoading(true);
          await deleteAttachment({
            contractId: actualContractId!,
            attachmentId: rowData.id,
          }).unwrap();
          toastElement?.show({
            severity: "success",
            summary: "Attachment Removed",
            detail: "Attachment file has been removed successfully.",
          });
        } catch (error: any) {
          const errorMessage =
            error?.data?.messages?.[0] ||
            error?.data?.message ||
            "Failed to remove attachment file.";
          toastElement?.show({
            severity: "error",
            summary: "Error",
            detail: errorMessage,
          });
        } finally {
          setIsLoading(false);
        }
      }}
    />
  );

  const newAttachmentFileActionsTemplate = (rowData: File, options: any) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={() => {
        const newFiles = formData.attachmentFiles.filter(
          (_, index) => index !== options.rowIndex
        );
        handleInputChange("attachmentFiles", newFiles);
      }}
    />
  );

  if (isLoadingContract) {
    return <div>Loading...</div>;
  }

  const contract = contractData?.data;
  const isActivated = contract?.status !== "Draft";

  const navigateToTermEdit = (newId: string) => {
    const base = hospitalId
      ? `/hospitals/view/${hospitalId}/contracts`
      : `/payer-rules/contracts`;
    navigate(`${base}/${newId}/edit`, { replace: true, state: location.state });
  };

  return (
    <div className="card">
      <div className="mb-3">
        <Button
          label="Return to Contract Details"
          icon="pi pi-arrow-left"
          className="p-button-text p-button-plain text-600"
          onClick={() => {
            const contractUrl = hospitalId
              ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}`
              : `/payer-rules/contracts/${actualContractId}`;
            navigate(contractUrl);
          }}
        />
      </div>
      <div className="mb-4 flex justify-content-between align-items-start flex-wrap gap-2">
        <h2 className="m-0">Edit Contract Fee Schedule</h2>
        <div className="flex gap-2 align-items-center flex-wrap">
          {termSwitcherOptions.length > 1 && (
            <Dropdown
              value={actualContractId}
              options={termSwitcherOptions}
              onChange={(e) => navigateToTermEdit(e.value)}
              placeholder="Switch term"
              className="min-w-12rem"
            />
          )}
        </div>
      </div>

      <div className="grid">
        {/* Basic Information */}
        <div className="col-12">
          <Card title="Basic Information" className="mb-4">
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Provider *
                </label>
                <Dropdown
                  value={formData.providerId}
                  options={providerOptions}
                  onChange={(e) => handleInputChange("providerId", e.value)}
                  placeholder="Select Provider"
                  className="w-full"
                  filter
                  disabled={isActivated}
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Payer *
                </label>
                <Dropdown
                  value={formData.payerId}
                  options={payerOptions}
                  onChange={(e) => handleInputChange("payerId", e.value)}
                  placeholder="Select Payer"
                  className="w-full"
                  filter
                  disabled={isActivated}
                />
              </div>
              <div className="col-12 md:col-4">
                <label className="block text-900 font-medium mb-2">
                  Effective Date *
                </label>
                <Calendar
                  value={formData.effectiveDate}
                  onChange={(e) => handleInputChange("effectiveDate", e.value)}
                  placeholder="Select Effective Date"
                  className={
                    effectiveDateDuplicatesOtherTerm
                      ? "w-full p-invalid"
                      : "w-full"
                  }
                  showIcon
                />
                {effectiveDateDuplicatesOtherTerm && (
                  <small className="text-red-500 block mt-1">
                    Another term in this contract already uses this effective
                    date. Choose a different day.
                  </small>
                )}
              </div>
              <div className="col-12 md:col-4">
                <label className="block text-900 font-medium mb-2">
                  Next Update Date
                </label>
                <Calendar
                  value={formData.nextUpdateDate}
                  onChange={(e) => handleInputChange("nextUpdateDate", e.value)}
                  placeholder="Select Next Update Date"
                  className="w-full"
                  showIcon
                />
              </div>
              <div className="col-12">
                <label className="block text-900 font-medium mb-2">Notes</label>
                <InputTextarea
                  value={formData.notes || ""}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  placeholder="Enter any notes or special instructions for this contract..."
                  rows={4}
                  className="w-full"
                  autoResize
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Medicare Advantage Payers - only show when primary payer is Medicare */}
        {isMedicarePayer && (
          <div className="col-12">
            <Card title="Medicare Advantage Payers" className="mb-4">
              <p className="mb-3 text-600">
                Select Medicare Advantage payers that should use this Medicare
                contract&apos;s procedures and pricing.
              </p>
              <MultiSelect
                value={formData.medicareAdvantagePayerIds}
                options={medicareAdvantagePayerOptions}
                onChange={(e) =>
                  handleInputChange("medicareAdvantagePayerIds", e.value)
                }
                placeholder="Select Medicare Advantage payers"
                className="w-full"
                display="chip"
                showClear
                filter
              />
              <small className="text-600 mt-2 block">
                When processing orders with these Medicare Advantage payers, the
                system will use this Medicare contract&apos;s procedures
                instead.
              </small>
            </Card>
          </div>
        )}

        {/* Rate Calculations */}
        <div className="col-12">
          <Card title="Rate Calculations" className="mb-4">
            <div className="mb-4">
              <div className="grid">
                <div className="col-12 md:col-5">
                  <label className="block text-900 font-medium mb-2">
                    Base Rate File
                  </label>
                  <Dropdown
                    value={newRateCalc.baseRateFileId}
                    options={baseRateFileOptions}
                    onChange={(e) =>
                      setNewRateCalc((prev) => ({
                        ...prev,
                        baseRateFileId: e.value,
                      }))
                    }
                    placeholder="Select Base Rate File"
                    className="w-full"
                    filter
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label className="block text-900 font-medium mb-2">
                    Type
                  </label>
                  <Dropdown
                    value={newRateCalc.rateType}
                    options={[
                      { label: "$", value: "unit" },
                      { label: "%", value: "percent" },
                    ]}
                    onChange={(e) => {
                      const newType = (e.value ?? "percent") as RateType;
                      setNewRateCalc((prev) => ({
                        ...prev,
                        rateType: newType,
                        ratePercent: 100,
                      }));
                    }}
                    placeholder="Select type"
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-3">
                  <label className="block text-900 font-medium mb-2">
                    {newRateCalc.rateType === "unit"
                      ? "Price per Unit"
                      : "Rate Percentage"}
                  </label>
                  <div className="p-inputgroup">
                    <InputNumber
                      value={
                        newRateCalc.rateType === "unit"
                          ? newRateCalc.ratePercent / 100
                          : newRateCalc.ratePercent
                      }
                      onValueChange={(e) =>
                        setNewRateCalc((prev) => ({
                          ...prev,
                          ratePercent:
                            prev.rateType === "unit"
                              ? (e.value || 0) * 100
                              : e.value || 0,
                        }))
                      }
                      placeholder={
                        newRateCalc.rateType === "unit"
                          ? "Price per Unit"
                          : "Rate %"
                      }
                      className="w-full"
                      mode="decimal"
                      min={0}
                      max={100000}
                      minFractionDigits={0}
                      maxFractionDigits={
                        newRateCalc.rateType === "unit" ? 4 : 2
                      }
                    />
                    {newRateCalc.rateType !== "unit" && (
                      <span className="p-inputgroup-addon">%</span>
                    )}
                  </div>
                </div>
                <div className="col-12 md:col-2 flex align-items-end">
                  <Button
                    label="Add Rate"
                    icon="pi pi-plus"
                    onClick={addNewRateCalculation}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <DataTable
              value={formData.rateCalculations}
              emptyMessage="No rate calculations added yet."
              className="mt-3"
            >
              <Column
                body={rateCalculationBodyTemplate}
                header="Rate Calculations"
              />
            </DataTable>
          </Card>
        </div>

        {/* Carve-Outs */}
        <div className="col-12">
          <Card title="Carve-Outs" className="mb-4">
            <p className="mb-3 text-600">
              Upload CSV files containing specific CPT codes and rates that
              override the standard calculations.
            </p>

            {/* Existing carveouts */}
            {contract?.carveOutFiles && contract.carveOutFiles.length > 0 && (
              <div className="mb-4">
                <h6>Existing Carveout Files</h6>
                <DataTable value={contract.carveOutFiles}>
                  <Column field="fileName" header="File Name" />
                  <Column
                    field="createdAt"
                    header="Uploaded"
                    body={(rowData) =>
                      new Date(rowData.createdAt).toLocaleDateString()
                    }
                  />
                  <Column
                    header="Actions"
                    body={carveoutFileActionsTemplate}
                    style={{ width: "100px" }}
                  />
                </DataTable>
              </div>
            )}

            {/* Upload new carveouts */}
            <FileUpload
              name="carveouts"
              multiple
              accept=".csv"
              maxFileSize={5000000}
              emptyTemplate={
                <div className="text-center p-4">
                  <i className="pi pi-cloud-upload text-4xl text-400" />
                  <p className="mt-2 mb-0">
                    Drag and drop CSV files here or click to browse.
                  </p>
                </div>
              }
              mode="basic"
              chooseOptions={{
                label: "Select CSV Files",
                icon: "pi pi-fw pi-plus",
              }}
              onSelect={(e) => {
                const files = Array.from(e.files);
                handleInputChange("carveoutFiles", [
                  ...formData.carveoutFiles,
                  ...files,
                ]);
              }}
            />

            {/* Display selected new carveout files */}
            {formData.carveoutFiles.length > 0 && (
              <div className="mt-4">
                <h6>New Files to Upload ({formData.carveoutFiles.length})</h6>
                <DataTable value={formData.carveoutFiles}>
                  <Column field="name" header="File Name" />
                  <Column
                    field="size"
                    header="Size"
                    body={(rowData) => `${(rowData.size / 1024).toFixed(2)} KB`}
                  />
                  <Column
                    header="Actions"
                    body={newCarveoutFileActionsTemplate}
                  />
                </DataTable>
              </div>
            )}
          </Card>
        </div>
        {/* Rule Set Selection */}
        <div className="col-12">
          <Card title="Rule Set Selection" className="mb-4">
            {/* MPDR Rule Sets */}
            <div className="mb-4">
              <label className="block text-900 font-medium mb-2">
                Multiple Procedure Discount Rule Sets
              </label>
              <MultiSelect
                value={formData.mpdrRulesetIds.filter((id) =>
                  mpdrRulesetOptions.some((opt) => opt.value === id)
                )}
                options={mpdrRulesetOptions}
                onChange={(e) => handleInputChange("mpdrRulesetIds", e.value)}
                placeholder="Select MPDR rule sets"
                className="w-full mb-2"
                display="chip"
                showClear
                filter
                filterBy="searchText"
                filterPlaceholder="Search by name, provider, or payer"
                showSelectAll={false}
              />

              {/* Show existing MPDR rulesets if provider/payer selected */}
              {existingMpdrRulesets.length > 0 && (
                <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                  <h6 className="mt-0 mb-2">
                    Existing MPDR Rule Sets for {selectedProvider?.name} -{" "}
                    {formData.payerId &&
                      payers?.data?.find((p: any) => p.id === formData.payerId)
                        ?.name}
                  </h6>
                  {existingMpdrRulesets.map((ruleset, index) => (
                    <div
                      key={ruleset.id}
                      className={
                        index < existingMpdrRulesets.length - 1
                          ? "pb-2 mb-2 border-bottom"
                          : ""
                      }
                    >
                      <div className="font-medium">
                        <a
                          href={`/payer-rules/mppr/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            // Only navigate if in admin context (hospital staff can't access these pages)
                            if (!hospitalId) {
                              navigate(`/payer-rules/mppr/edit/${ruleset.id}`);
                            }
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName || "Unnamed MPDR Rule Set"}
                        </a>
                      </div>
                      <div className="text-sm text-600 mt-1">
                        Rank 2: {ruleset.discountPercentForRank2}%, Rank 3:{" "}
                        {ruleset.discountPercentForRank3}%, Rank 4:{" "}
                        {ruleset.discountPercentForRank4}%, Rank 5+:{" "}
                        {ruleset.discountPercentForRank5Plus}%
                      </div>
                      <div className="text-sm text-600">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                          <span>
                            CPT Codes:{" "}
                            {ruleset.cptCodes.slice(0, 10).join(", ")}
                            {ruleset.cptCodes.length > 10 && (
                              <span>
                                , and {ruleset.cptCodes.length - 10} other CPT
                                codes
                              </span>
                            )}
                          </span>
                        ) : (
                          "No CPT codes configured"
                        )}
                        {ruleset.allowOnDuplicateCptCodes &&
                          " • Allows duplicate codes"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-top pt-4">
              {/* Case Rate Rule Sets */}
              <label className="block text-900 font-medium mb-2">
                Case Rate Rule Sets
              </label>
              <MultiSelect
                value={formData.caseRateRulesetIds.filter((id) =>
                  caseRateRulesetOptions.some((opt) => opt.value === id)
                )}
                options={caseRateRulesetOptions}
                onChange={(e) =>
                  handleInputChange("caseRateRulesetIds", e.value)
                }
                placeholder="Select case rate rule sets"
                className="w-full mb-2"
                display="chip"
                showClear
                filter
                filterBy="searchText"
                filterPlaceholder="Search by name, provider, or payer"
                showSelectAll={false}
              />

              {/* Show existing case rate rulesets if provider/payer selected */}
              {existingCaseRateRulesets.length > 0 && (
                <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                  <h6 className="mt-0 mb-2">
                    Existing Case Rate Rule Sets for {selectedProvider?.name} -{" "}
                    {formData.payerId &&
                      payers?.data?.find((p: any) => p.id === formData.payerId)
                        ?.name}
                  </h6>
                  {existingCaseRateRulesets.map((ruleset, index) => (
                    <div
                      key={ruleset.id}
                      className={
                        index < existingCaseRateRulesets.length - 1
                          ? "pb-2 mb-2 border-bottom"
                          : ""
                      }
                    >
                      <div className="font-medium">
                        <a
                          href={`/payer-rules/case-rate/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(
                              `/payer-rules/case-rate/edit/${ruleset.id}`
                            );
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName || "Unnamed Case Rate Rule Set"}
                        </a>
                      </div>
                      <div className="text-sm text-600 mt-1">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                          <div>
                            Included CPT Codes:{" "}
                            {ruleset.cptCodes.slice(0, 10).join(", ")}
                            {ruleset.cptCodes.length > 10 && (
                              <span>
                                , and {ruleset.cptCodes.length - 10} other CPT
                                codes
                              </span>
                            )}
                          </div>
                        ) : (
                          <div>No included CPT codes</div>
                        )}
                        {ruleset.excludedCptCodes &&
                        ruleset.excludedCptCodes.length > 0 ? (
                          <div>
                            Excluded CPT Codes:{" "}
                            {ruleset.excludedCptCodes.slice(0, 10).join(", ")}
                            {ruleset.excludedCptCodes.length > 10 && (
                              <span>
                                , and {ruleset.excludedCptCodes.length - 10}{" "}
                                other CPT codes
                              </span>
                            )}
                          </div>
                        ) : (
                          <div>No excluded CPT codes</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Inpatient Rulesets */}
        <div className="col-12">
          <Card
            title={
              <div className="flex justify-content-between align-items-center w-full">
                <span>Inpatient Rulesets</span>
                {!hospitalId && (
                  <Button
                    label="Create New"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-sm"
                    onClick={() =>
                      navigate("/payer-rules/inpatient-rulesets/new")
                    }
                  />
                )}
              </div>
            }
            className="mb-4"
          >
            <MultiSelect
              value={formData.inpatientRulesetIds.filter((rId) =>
                inpatientRulesetOptions.some((opt) => opt.value === rId)
              )}
              options={inpatientRulesetOptions}
              onChange={(e) =>
                handleInputChange("inpatientRulesetIds", e.value)
              }
              placeholder="Select inpatient rulesets"
              className="w-full mb-2"
              display="chip"
              showClear
              filter
              filterBy="searchText"
              filterPlaceholder="Search by name, provider, or payer"
              showSelectAll={false}
            />

            {existingInpatientRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                <h6 className="mt-0 mb-2">
                  Existing Inpatient Rulesets for {selectedProvider?.name} -{" "}
                  {formData.payerId &&
                    payers?.data?.find((p: any) => p.id === formData.payerId)
                      ?.name}
                </h6>
                {existingInpatientRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingInpatientRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/inpatient-rulesets/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!hospitalId) {
                            navigate(
                              `/payer-rules/inpatient-rulesets/edit/${ruleset.id}`
                            );
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.name || "Unnamed Inpatient Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      Type: {ruleset.type || "—"}
                    </div>
                    <div className="text-sm text-600">
                      DRG Codes:{" "}
                      {ruleset.drgCodes && ruleset.drgCodes.length > 0
                        ? ruleset.drgCodes.join(", ")
                        : "No codes configured"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Exploding Fee Schedule Rulesets */}
        <div className="col-12">
          <Card
            title={
              <div className="flex justify-content-between align-items-center w-full">
                <span>Exploding Fee Schedule Rulesets</span>
                {!hospitalId && (
                  <Button
                    label="Create New"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-sm"
                    onClick={() =>
                      navigate("/payer-rules/exploding-fee-schedules/new")
                    }
                  />
                )}
              </div>
            }
            className="mb-4"
          >
            <MultiSelect
              value={formData.explodingFeeScheduleRulesetIds.filter((rId) =>
                explodingRulesetOptions.some((opt) => opt.value === rId)
              )}
              options={explodingRulesetOptions}
              onChange={(e) =>
                handleInputChange("explodingFeeScheduleRulesetIds", e.value)
              }
              placeholder="Select exploding fee schedule rulesets"
              className="w-full mb-2"
              display="chip"
              showClear
              filter
              filterBy="searchText"
              filterPlaceholder="Search by name, provider, or payer"
              showSelectAll={false}
            />

            {existingExplodingRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                <h6 className="mt-0 mb-2">
                  Existing Exploding Fee Schedule Rulesets for{" "}
                  {selectedProvider?.name} -{" "}
                  {formData.payerId &&
                    payers?.data?.find((p: any) => p.id === formData.payerId)
                      ?.name}
                </h6>
                {existingExplodingRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingExplodingRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!hospitalId) {
                            navigate(
                              `/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`
                            );
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName ||
                          "Unnamed Exploding Fee Schedule Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      CPT Codes:{" "}
                      {ruleset.cptCodes && ruleset.cptCodes.length > 0
                        ? ruleset.cptCodes.join(", ")
                        : "No codes configured"}
                    </div>
                    <div className="text-sm text-600">
                      Total Rate: $
                      {ruleset.totalRateInCents != null
                        ? (ruleset.totalRateInCents / 100).toLocaleString(
                            "en-US",
                            { minimumFractionDigits: 2 }
                          )
                        : "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Package Rulesets */}
        <div className="col-12">
          <Card
            title={
              <div className="flex justify-content-between align-items-center w-full">
                <span>Package Rulesets</span>
                {!hospitalId && (
                  <Button
                    label="Create New"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-sm"
                    onClick={() => navigate("/payer-rules/packages/new")}
                  />
                )}
              </div>
            }
            className="mb-4"
          >
            <MultiSelect
              value={formData.packageRulesetIds.filter((rId) =>
                packageRulesetOptions.some((opt) => opt.value === rId)
              )}
              options={packageRulesetOptions}
              onChange={(e) => handleInputChange("packageRulesetIds", e.value)}
              placeholder="Select package rulesets"
              className="w-full mb-2"
              display="chip"
              showClear
              filter
              filterBy="searchText"
              filterPlaceholder="Search by name, provider, or payer"
              showSelectAll={false}
            />

            {existingPackageRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                <h6 className="mt-0 mb-2">
                  Existing Package Rulesets for {selectedProvider?.name} -{" "}
                  {formData.payerId &&
                    payers?.data?.find((p: any) => p.id === formData.payerId)
                      ?.name}
                </h6>
                {existingPackageRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingPackageRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/packages/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!hospitalId) {
                            navigate(
                              `/payer-rules/packages/edit/${ruleset.id}`
                            );
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName || "Unnamed Package Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      Trigger CPT Codes:{" "}
                      {ruleset.triggerCptCodes &&
                      ruleset.triggerCptCodes.length > 0
                        ? ruleset.triggerCptCodes.join(", ")
                        : "No trigger codes configured"}
                    </div>
                    <div className="text-sm text-600">
                      Bundled CPT Codes:{" "}
                      {ruleset.bundledCptCodes &&
                      ruleset.bundledCptCodes.length > 0
                        ? ruleset.bundledCptCodes.join(", ")
                        : "No bundled codes configured"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Contract Attachments */}
        <div className="col-12">
          <Card title="Contract Attachments" className="mb-4">
            <p className="mb-3 text-600">
              Attach files to this contract for reference and download (PDFs,
              Excel, images, etc. up to 50MB each).
            </p>

            {/* Existing attachments */}
            {contract?.attachments && contract.attachments.length > 0 && (
              <div className="mb-4">
                <h6>Existing Attachment Files</h6>
                <DataTable value={contract.attachments}>
                  <Column field="fileName" header="File Name" />
                  <Column
                    field="fileSize"
                    header="Size"
                    body={(rowData) => {
                      const kb = rowData.fileSize / 1024;
                      return kb >= 1024
                        ? `${(kb / 1024).toFixed(2)} MB`
                        : `${kb.toFixed(2)} KB`;
                    }}
                  />
                  <Column
                    field="createdAt"
                    header="Uploaded"
                    body={(rowData) =>
                      new Date(rowData.createdAt).toLocaleDateString()
                    }
                  />
                  <Column
                    header="Actions"
                    body={attachmentFileActionsTemplate}
                    style={{ width: "100px" }}
                  />
                </DataTable>
              </div>
            )}

            {/* Upload new attachments */}
            <FileUpload
              name="attachments"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.webp,.ppt,.pptx,.odt,.ods,.odp,.zip"
              maxFileSize={52428800}
              emptyTemplate={
                <div className="text-center p-4">
                  <i className="pi pi-cloud-upload text-4xl text-400" />
                  <p className="mt-2 mb-0">
                    Drag and drop files here or click to browse.
                  </p>
                </div>
              }
              mode="basic"
              chooseOptions={{
                label: "Select Files",
                icon: "pi pi-fw pi-plus",
              }}
              onSelect={(e) => {
                const files = Array.from(e.files);
                handleInputChange("attachmentFiles", [
                  ...formData.attachmentFiles,
                  ...files,
                ]);
              }}
            />

            {/* Display selected new attachment files */}
            {formData.attachmentFiles.length > 0 && (
              <div className="mt-4">
                <h6>New Files to Upload ({formData.attachmentFiles.length})</h6>
                <DataTable value={formData.attachmentFiles}>
                  <Column
                    header="File Name"
                    body={(rowData: File) => rowData.name}
                  />
                  <Column
                    field="size"
                    header="Size"
                    body={(rowData) => {
                      const kb = rowData.size / 1024;
                      return kb >= 1024
                        ? `${(kb / 1024).toFixed(2)} MB`
                        : `${kb.toFixed(2)} KB`;
                    }}
                  />
                  <Column
                    header="Actions"
                    body={newAttachmentFileActionsTemplate}
                  />
                </DataTable>
              </div>
            )}
          </Card>
        </div>

        {/* Actions */}
        <div className="col-12">
          <div className="flex justify-content-between">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                const contractUrl = hospitalId
                  ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}`
                  : `/payer-rules/contracts/${actualContractId}`;
                navigate(contractUrl);
              }}
            />
            <Button
              label="Save Changes"
              icon="pi pi-check"
              onClick={handleSubmit}
              disabled={isSubmitting || effectiveDateDuplicatesOtherTerm}
              loading={isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditContractFeeSchedulePage;
